import { supabase } from '../lib/supabase';

/**
 * Marca activa del usuario para una empresa. **Sólo lectura.**
 *
 * Mismo orden de precedencia que `useBrandProfile` y el que tiene inline
 * `SeleccionarProductosAnuncio`:
 *   1) `profiles.current_brand_id` (si sigue siendo una marca no-draft de la empresa)
 *   2) la marca default de la empresa
 *   3) la marca no-draft más vieja
 *
 * Devuelve `null` cuando la empresa no tiene marcas: en ese caso el caller
 * **no** debe filtrar por marca (filtrar por `null` esconde todo el catálogo).
 *
 * A diferencia de `resolveProjectBrandId` (`utils/videoProject`), NO crea una
 * marca default cuando no hay ninguna. Esto se usa desde pantallas de lectura,
 * donde una escritura silenciosa al abrir un listado sería un efecto no deseado.
 */
export async function resolveMarcaActivaId(
  companyId: string,
): Promise<string | null> {
  if (!companyId) return null;

  const { data: brandsData } = await supabase
    .from('brand_profiles')
    .select('id, is_default')
    .eq('id_company', companyId)
    .eq('is_draft', false)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true });

  const list =
    (brandsData as Array<{ id: string; is_default: boolean }> | null) ?? [];
  if (list.length === 0) return null;

  const fallback = list.find((b) => b.is_default)?.id ?? list[0].id;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return fallback;

  const { data: prof } = await supabase
    .from('profiles')
    .select('current_brand_id')
    .eq('id', user.id)
    .maybeSingle();

  const fromProfile =
    (prof as { current_brand_id?: string | null } | null)?.current_brand_id ??
    null;

  // Se valida contra la lista a propósito: un `current_brand_id` que apunta a
  // una marca borrada, pasada a draft o de otra empresa no puede ser la marca
  // activa, y filtrar por él dejaría el catálogo vacío en vez de mostrar la
  // marca default. Es la misma validación que hace `resolveProjectBrandId`.
  if (fromProfile && list.some((b) => b.id === fromProfile)) return fromProfile;

  return fallback;
}
